import React from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@components/ui/dialog";
import { useQuery, useMutation } from "react-query";
import {
  distributedSystemsType,
  deviceTimeSetup,
  airgapOnOffSetting,
  deviceIpSetup,
  setupWizard,
} from "@services/api/home";
import { Button } from "@components/ui/button";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircle,
  AlertCircle,
  Loader,
  AlertCircleIcon,
} from "lucide-react";
import { Checkbox } from "@components/ui/checkbox";
import CustomSelect from "@components/common/CustomSelect";
import { Switch } from "@components/ui/switch";
import { Label } from "@components/ui/label";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Input } from "@components/ui/input";
import { useToast } from "@components/ui/use-toast";
import {
  loadJsonData,
  fetchDeviceProperties,
  getSystemArchitectureInfo,
} from "@services/api/commonApis";

const SetupWizard = () => {
  const { toast } = useToast();
  const [pageNum, setPageNum] = React.useState(0);
  const [termCheck, setTermCheck] = React.useState(false);
  const ipAddressRef = React.useRef<HTMLInputElement>(null);
  const subnetMaskRef = React.useRef<HTMLInputElement>(null);
  const defaultGatewayRef = React.useRef<HTMLInputElement>(null);
  const dnsServerRef = React.useRef<HTMLInputElement>(null);
  const [formData, setFormData] = React.useState<any>({
    arch: {
      value: "",
      status: "",
    },
    master: {
      value: false,
      status: "",
    },
    ip: {
      value: "",
      status: "",
    },
    subnet: {
      value: "",
      status: "",
    },
    gateway: {
      value: "",
      status: "",
    },
    dns: {
      value: "",
      status: "",
    },
    timezone: {
      value: "",
      status: "",
    },
    airgap: {
      value: false,
      status: "",
    },
  });

  const { data: timezoneData } = useQuery(["timezonedata"], () =>
    loadJsonData(process.env.STATIC_PATH + "/json/timezones.json"),
  );

  const { data: deviceProperties, isLoading: isDevicePropertiesLoading } =
    useQuery(["fetchDeviceProperties"], () => fetchDeviceProperties());

  const {
    data: systemArchitectureInfo,
    isLoading: isSystemArchitectureInfoLoading,
  } = useQuery(["systemArchitectureInfo"], ({ signal }) =>
    getSystemArchitectureInfo(signal),
  );

  const watchDistributedSystem = useMutation(distributedSystemsType, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Device IP configuration updated",
        variant: "success",
      });
      setFormData((prev: any) => ({
        ...prev,
        arch: { ...prev.arch, status: "success" },
      }));
    },
    onError: () => {
      toast({
        title: "Error",
        description: "An error occured!",
        variant: "destructive",
      });
      setFormData((prev: any) => ({
        ...prev,
        arch: { ...prev.arch, status: "error" },
      }));
    },
  });

  const watchTimezoneSetup = useMutation(deviceTimeSetup, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Timezone updated",
        variant: "success",
      });
      setFormData((prev: any) => ({
        ...prev,
        timezone: { ...prev.timezone, status: "success" },
      }));
    },
    onError: () => {
      toast({
        title: "Error",
        description: "An error occured!",
        variant: "destructive",
      });
      setFormData((prev: any) => ({
        ...prev,
        timezone: { ...prev.timezone, status: "error" },
      }));
    },
  });

  const watchAirgap = useMutation(airgapOnOffSetting, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Airgap setting updated",
        variant: "success",
      });
      setFormData((prev: any) => ({
        ...prev,
        airgap: { ...prev.airgap, status: "success" },
      }));
    },
    onError: () => {
      toast({
        title: "Error",
        description: "An error occured!",
        variant: "destructive",
      });
      setFormData((prev: any) => ({
        ...prev,
        airgap: { ...prev.airgap, status: "error" },
      }));
    },
  });

  const watchDeviceIp = useMutation(deviceIpSetup, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Your request has been submitted",
        variant: "success",
      });
      setFormData((prev: any) => ({
        ...prev,
        ip: { ...prev.ip, status: "success" },
      }));
    },
    onError: () => {
      toast({
        title: "Error",
        description: "An error occured!",
        variant: "destructive",
      });
      setFormData((prev: any) => ({
        ...prev,
        ip: { ...prev.ip, status: "error" },
      }));
    },
  });

  const watchFinish = useMutation(setupWizard, {
    onSuccess: () => {
      window.open("/", "_self");
    },
    onError: () => {
      toast({
        title: "Error",
        description: "An error occured!",
        variant: "destructive",
      });
    },
  });

  React.useEffect(() => {
    if (!deviceProperties) return;

    if (ipAddressRef.current)
      ipAddressRef.current.value = deviceProperties.ip ?? "";
    if (subnetMaskRef.current)
      subnetMaskRef.current.value = deviceProperties.prefix ?? "";
    if (defaultGatewayRef.current)
      defaultGatewayRef.current.value = deviceProperties.gateway ?? "";
    if (dnsServerRef.current)
      dnsServerRef.current.value = deviceProperties.dns ?? "";

    setFormData((prev: any) => ({
      ...prev,
      ip: {
        value: deviceProperties.ip ?? "",
        status: deviceProperties.ip ? "success" : "",
      },
      subnet: {
        value: deviceProperties.prefix ?? "",
        status: deviceProperties.prefix ? "success" : "",
      },
      gateway: {
        value: deviceProperties.gateway ?? "",
        status: deviceProperties.gateway ? "success" : "",
      },
      dns: {
        value: deviceProperties.dns ?? "",
        status: deviceProperties.dns ? "success" : "",
      },
      timezone: {
        value: deviceProperties.current_timezone
          ? deviceProperties.current_timezone.replace("/", "_")
          : "",
        status: deviceProperties.current_timezone ? "success" : "",
      },
    }));
  }, [deviceProperties]);

  React.useEffect(() => {
    if (!systemArchitectureInfo) return;

    setFormData((prev: any) => ({
      ...prev,
      arch: {
        value: systemArchitectureInfo.distributedSystemsType ?? prev.arch.value,
        status: systemArchitectureInfo.distributedSystemsType
          ? "success"
          : prev.arch.status,
      },
      master: {
        value:
          "master" in (systemArchitectureInfo.distributedSystemsCount ?? {}) &&
          systemArchitectureInfo.distributedSystemsCount.master > 0,
        status:
          "master" in (systemArchitectureInfo.distributedSystemsCount ?? {}) &&
          systemArchitectureInfo.distributedSystemsCount.master > 0
            ? "success"
            : prev.master.status,
      },
      airgap: {
        value:
          "airgap" in systemArchitectureInfo &&
          systemArchitectureInfo.airgap === 1,
        status:
          "airgap" in systemArchitectureInfo &&
          systemArchitectureInfo.airgap === 1
            ? "success"
            : prev.airgap.status,
      },
    }));
  }, [systemArchitectureInfo]);

  React.useEffect(() => {
    if (
      formData.ip.value &&
      ipAddressRef.current &&
      !ipAddressRef.current.value
    ) {
      ipAddressRef.current.value = formData.ip.value;
    }
    if (
      formData.subnet.value &&
      subnetMaskRef.current &&
      !subnetMaskRef.current.value
    ) {
      subnetMaskRef.current.value = formData.subnet.value;
    }
    if (
      formData.gateway.value &&
      defaultGatewayRef.current &&
      !defaultGatewayRef.current.value
    ) {
      defaultGatewayRef.current.value = formData.gateway.value;
    }
    if (
      formData.dns.value &&
      dnsServerRef.current &&
      !dnsServerRef.current.value
    ) {
      dnsServerRef.current.value = formData.dns.value;
    }
  }, [
    formData.ip.value,
    formData.subnet.value,
    formData.gateway.value,
    formData.dns.value,
  ]);

  const [timezoneValues, setTimezoneValues] = React.useState([] as any[]);
  React.useEffect(() => {
    if (timezoneData) {
      const temp: any = [];
      const time = timezoneData;
      time.forEach((element: any) => {
        temp.push({ label: element, value: element.replace("/", "_") });
      });
      setTimezoneValues(temp);
    }
  }, [timezoneData]);

  function termCheckChanged(e: boolean) {
    if (e) {
      document.getElementById("termsError")?.classList.add("hidden");
    }
    setTermCheck(e);
  }

  function changePage(page: number) {
    if (page == 2 && !termCheck) {
      document.getElementById("termsError")?.classList.remove("hidden");
      return;
    }
    document.getElementById("termsError")?.classList.add("hidden");
    setPageNum(page);
  }

  function submitSystemArch() {
    if (formData.arch.value == "") {
      setFormData((prev: any) => ({
        ...prev,
        arch: { ...prev.arch, status: "invalid" },
      }));
      return;
    }
    setFormData((prev: any) => ({
      ...prev,
      arch: { ...prev.arch, status: "loading" },
    }));
    watchDistributedSystem.mutate({
      distributed_systems_type: formData.arch.value,
      distributed_systems_master: formData.master.value ? 1 : 0,
    });
  }

  function submitTimeZone() {
    if (formData.timezone.value == "") {
      setFormData((prev: any) => ({
        ...prev,
        timezone: { ...prev.timezone, status: "invalid" },
      }));
      return;
    }
    setFormData((prev: any) => ({
      ...prev,
      timezone: { ...prev.timezone, status: "loading" },
    }));
    watchTimezoneSetup.mutate({
      timezone: formData.timezone.value.replace("_", "/"),
    });
  }

  function submitAirgap(e: boolean) {
    setFormData((prev: any) => ({
      ...prev,
      airgap: { value: e, status: "loading" },
    }));
    watchAirgap.mutate({ status: e ? 1 : 0 });
  }

  function isValidIP(ip: string) {
    const _ips = ip.split(",").map((s) => s.trim());
    let is_valid = true;
    const ipRegex =
      /^(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])(\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])){3}$/;
    // return ipRegex.test(ip);

    _ips.map((_ip) => {
      if (!ipRegex.test(_ip)) is_valid = false;
    });
    return is_valid;
  }

  function isValidSubnetMask(mask: string) {
    if (mask.includes(".")) {
      const subnetRegex =
        /^(255|254|252|248|240|224|192|128|0)\.(255|254|252|248|240|224|192|128|0)\.(255|254|252|248|240|224|192|128|0)\.(255|254|252|248|240|224|192|128|0)$/;

      if (!subnetRegex.test(mask)) return false;

      const binaryMask = mask
        .split(".")
        .map((octet: any) => parseInt(octet, 10).toString(2).padStart(8, "0"))
        .join("");

      return /^1*0*$/.test(binaryMask);
    } else {
      const cidr = parseInt(mask);
      if (isNaN(cidr) || cidr < 0 || cidr > 32) return false;
      return true;
    }
  }

  function submitipConfig() {
    let valid = true;
    const ipAdd: string = ipAddressRef.current
      ? ipAddressRef.current.value
      : "";
    const subnet: string = subnetMaskRef.current
      ? subnetMaskRef.current.value
      : "";
    const gateway: string = defaultGatewayRef.current
      ? defaultGatewayRef.current.value
      : "";
    const dns: string = dnsServerRef.current ? dnsServerRef.current.value : "";

    const newStatus = {
      ip: isValidIP(ipAdd) ? "" : "invalid",
      subnet: isValidSubnetMask(subnet) ? "" : "invalid",
      gateway: isValidIP(gateway) ? "" : "invalid",
      dns: isValidIP(dns) ? "" : "invalid",
    };

    if (Object.values(newStatus).some((s) => s === "invalid")) {
      valid = false;
    }

    setFormData((prev: any) => ({
      ...prev,
      ip: { value: ipAdd, status: valid ? "loading" : newStatus.ip },
      subnet: {
        value: subnet,
        status: valid ? prev.subnet.status : newStatus.subnet,
      },
      gateway: {
        value: gateway,
        status: valid ? prev.gateway.status : newStatus.gateway,
      },
      dns: { value: dns, status: valid ? prev.dns.status : newStatus.dns },
    }));

    if (valid) {
      watchDeviceIp.mutate({
        ipAddress: ipAdd.replace(/\s+/g, ""),
        subnetMask: subnet.replace(/\s+/g, ""),
        gateWay: gateway.replace(/\s+/g, ""),
        dnsServer: dns.replace(/\s+/g, ""),
      });
    }
  }

  function validateForm() {
    if (formData.arch.value == "") {
      toast({
        title: "Error",
        description: "System Architecture Required",
        variant: "destructive",
      });
      return false;
    }
    if (formData.arch.status == "invalid") {
      toast({
        title: "Error",
        description: "Valid System Architecture Required",
        variant: "destructive",
      });
      return false;
    } else if (formData.arch.status != "success") {
      toast({
        title: "Error",
        description:
          "System Architecture has not been submitted. Please submit and ensure it is successful before finishing setup.",
        variant: "destructive",
      });
    }
    if (formData.ip.status == "invalid") {
      toast({
        title: "Error",
        description: "Valid IP address Required",
        variant: "destructive",
      });
      return false;
    }
    if (formData.subnet.status == "invalid") {
      toast({
        title: "Error",
        description: "Valid Subnet Mask Required",
        variant: "destructive",
      });
      return false;
    }
    if (formData.gateway.status == "invalid") {
      toast({
        title: "Error",
        description: "Valid Default Gateway Required",
        variant: "destructive",
      });
      return false;
    }
    if (formData.dns.status == "invalid") {
      toast({
        title: "Error",
        description: "Valid DNS Server Required",
        variant: "destructive",
      });
      return false;
    }
    if (formData.timezone.status == "invalid") {
      toast({
        title: "Error",
        description: "Valid Timezone Required",
        variant: "destructive",
      });
      return false;
    }
    return true;
  }

  function finishSetup() {
    const valid = validateForm();
    if (valid) {
      watchFinish.mutate({ action: "finish" });
    }
  }

  return (
    <>
      <div className="fixed top-0 left-0 w-full h-full bg-primary-background z-50"></div>
      <Dialog open={true} modal={false}>
        <DialogContent className="max-w-[45vw] block [&>.dialog-close-btn]:hidden">
          <DialogHeader className="font-bold text-primary underline underline-offset-4 mb-6">
            Setup Wizard
          </DialogHeader>
          <div className="h-[80vh] overflow-y-auto">
            {/* First Page */}
            <div className={"setup-page " + (pageNum == 0 ? "" : "hidden")}>
              <div className="text-xl mb-4">Welcome to LinkShadow</div>
              <div className="text-sm">
                Let&apos;s get started with the setup wizard
              </div>
            </div>

            {/* Second Page */}
            <div
              className={"setup-page text-xs " + (pageNum == 1 ? "" : "hidden")}
            >
              <div className="text-xl">
                Legal - LinkShadow&apos;s Privacy & Data Protection Policy
              </div>
              <br />
              <p>
                LinkShadow (hereinafter “LinkShadow” or “We” or “Us”) respect
                your privacy and are committed to best practices and the use of
                appropriate measures to protect any personal data.
              </p>
              <br />
              <p>
                Please note that LinkShadow collects, uses and keeps information
                in compliance with the General Data Protection Regulation
                (Regulation (EU) 2016/679) (“GDPR”), the California Consumer
                Privacy Act (“CCPA”), the UK Data Protection Act 2018, the
                Privacy and Electronic Communications (EC Directive) Regulations
                2003, and all relevant data protection regulations.
              </p>
              <br />
              <p>
                LinkShadow (its affiliates and subsidiaries) issues this Privacy
                Policy (the “Policy”) which sets out the basis on which your
                information, will be collected and processed by Us. Your visit
                to{" "}
                <a className="text-blue-500" href="https://www.linkshadow.com">
                  https://www.linkshadow.com
                </a>{" "}
                or the use of any LinkShadow products or services is an
                acceptance and consent to all practices as described in this
                Privacy Policy.
              </p>
              <br />
              <div className="text-xl">What do we mean by User Information</div>
              <br />
              <p>
                Any information that may be used to identify an individual,
                including but not limited to:
              </p>
              <br />
              <ul className="ps-4">
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <b>Contact and identification information</b> such as your
                      name, email, address and phone number.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <b>Login Information</b> such as your user ID and
                      password.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle className="text-primary me-2" size={14} />
                    </div>
                    <div className="flex-grow-1">
                      <b>Technical Data</b> including your Internet Protocol
                      (IP) address, health checks and support, login data,
                      operating system and web browser type and version, traffic
                      data, location data and other communication data, and the
                      resources that you access.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <b>Usage Data</b> including how you use our website,
                      products and services.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <b>Profile Data</b> including usernames, passwords, and
                      feedback data.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <b>Marketing and Communications Data</b> including your
                      preferences in receiving marketing from us and your
                      communication preferences.
                    </div>
                  </div>
                </li>
              </ul>
              <br />
              <div className="text-xl">
                How usually user information is collected
              </div>
              <br />
              <p>
                LinkShadow may collect information about you or information that
                you provide to us (“User Information”) through the following:
              </p>
              <br />
              <ul className="ps-4">
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      As you interact with our website, products or services, we
                      will collect Technical Data about your equipment, browsing
                      actions and patterns. We collect this personal data by
                      using cookies and other similar technologies.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      Provide contact details (e.g. giving business cards) at a
                      marketing event.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      Request and receive marketing communications.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      Through a job application that you have submitted or
                      inquiry, you may provide us with a copy of your CV or
                      other relevant information. We may use this information
                      for the purpose of considering your application or
                      inquiry. Except when you explicitly request otherwise, we
                      may keep this information on file for future reference.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      We may source some of your information which is publicly
                      available on the internet, such as from Facebook,
                      LinkedIn, Twitter and Google, or from other sources, or
                      any information that we acquire from third party service
                      providers (such as business partners, sub-contractors for
                      any technical delivery services, advertising networks,
                      analytics providers, search information providers, credit
                      reference agencies). Wherever we collect information about
                      you from third parties we ensure that we are legally
                      entitled to do so.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <p>
                        When you purchase or use our products or services, we
                        may use your personal data for purposes which include
                        but are not limited to:
                      </p>
                      <ul className="ps-4">
                        <br />
                        <li>
                          <div className="flex">
                            <div className="inline-block">
                              <CheckCircle
                                className="text-primary inline-block me-2"
                                size={14}
                              />
                            </div>
                            <div className="flex-grow-1">
                              Verifying your credentials
                            </div>
                          </div>
                        </li>
                        <br />
                        <li>
                          <div className="flex">
                            <div className="inline-block">
                              <CheckCircle
                                className="text-primary inline-block me-2"
                                size={14}
                              />
                            </div>
                            <div className="flex-grow-1">
                              Carrying out end user compliance checks for export
                              control purposes,
                            </div>
                          </div>
                        </li>
                        <br />
                        <li>
                          <div className="flex">
                            <div className="inline-block">
                              <CheckCircle
                                className="text-primary inline-block me-2"
                                size={14}
                              />
                            </div>
                            <div className="flex-grow-1">
                              Processing orders and generating billing
                              information.
                            </div>
                          </div>
                        </li>
                        <br />
                      </ul>
                    </div>
                  </div>
                </li>
              </ul>
              <br />
              <div className="text-xl">
                How we may use user information & lawful basis of usage
              </div>
              <br />
              <p>
                Collected User Information held about you may be used by
                LinkShadow in the following circumstances:
              </p>
              <br />
              <ul className="ps-4">
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      To perform a contract we are about to enter into or have
                      entered into with you, including notification of changes
                      to our products and services.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      Keeping our records updated and examine how Users use our
                      products or services.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      Where we need to comply with a legal obligation.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      To provide you with information, products or services that
                      you request from us, or which LinkShadow feel may interest
                      you, where you have consented to be contacted for such
                      purposes.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      To allow you to participate in interactive features of our
                      products or service, when you choose to do so, and develop
                      our products and services to reach the highest level of
                      User satisfaction.
                    </div>
                  </div>
                </li>
              </ul>
              <br />
              <div className="text-xl">Marketing</div>
              <br />
              <p>
                Generally, most of the time we will get your consent before
                sending direct marketing communications to you via email or text
                message. You have the right to unsubscribe or withdraw consent
                to marketing at any time by contacting us.
              </p>
              <br />
              <p>
                If you are an existing customer, LinkShadow will only contact
                you by electronic means (e-mail or SMS) with information about
                goods and services similar to those that were the subject of a
                previous sale to you.
              </p>
              <br />
              <p>
                You can ask us to stop sending you marketing messages at any
                time by following the opt-out links on any marketing message
                sent to you OR by contacting us via email at any time. Where you
                opt out of receiving these marketing messages, this will not
                apply to personal data provided to us as a result of a
                product/service purchase.
              </p>
              <br />
              <p>
                We will not sell or rent your data to third parties or share
                your data with third parties for marketing purposes. We may use
                third party software to send you information for marketing
                purposes, but such third parties will not have access to or be
                able to read your personal information.
              </p>
              <br />
              <p>
                If you receive an email which claims to come from us but does
                not use our domain, or if you are suspicious that an email may
                not be approved by us, then please send a copy of the email to{" "}
                <a className="text-blue-500" href="mailto:info@linkshadow.com">
                  info@linkshadow.com
                </a>{" "}
                so we can investigate.
              </p>
              <br />
              <div className="text-xl">Cookies</div>
              <br />
              <p>
                You can set your browser to refuse all or some browser cookies,
                or to alert you when websites set or access cookies. If you
                disable or refuse cookies, please note that some parts of this
                website may become inaccessible or not function properly. For
                more information about the cookies we use, please see our cookie
                policy.
              </p>
              <br />
              <div className="text-xl">Storing user information</div>
              <br />
              <p>
                Your personal data is securely stored by LinkShadow on the
                LinkShadow servers. LinkShadow has set up systems and processes
                to prevent unauthorized access or disclosure of User
                Information.
              </p>
              <br />
              <div className="text-xl">Transfer of user information</div>
              <br />
              <p>
                As a global company, we have international sites and users all
                over the world. When you give us personal data, that data may be
                used, processed or stored anywhere in the world, including
                countries outside the European Economic Area (“EEA”). It may
                also be processed by staff operating outside the EEA, who work
                for us or for one of our suppliers. LinkShadow places
                substantial importance on protecting the confidentiality of
                personal information and seeks the cooperation of all its
                suppliers in furthering this goal. LinkShadow will only transfer
                personal information to a supplier where the supplier has
                provided assurances that they will provide at least the same
                level of privacy protection as is required by this Policy. Where
                LinkShadow has knowledge that a supplier is using or sharing
                personal information in a way that is contrary to this policy,
                LinkShadow will take reasonable steps to prevent or stop such
                processing.
              </p>
              <div className="text-xl">Keeping personal data secured</div>
              <br />
              <p>
                LinkShadow endeavours to hold all personal data securely in
                accordance with our internal security procedures and applicable
                laws and regulations. We update and test our security on an
                ongoing basis. LinkShadow will do its best to protect User
                Information, but LinkShadow cannot guarantee the security of
                your data transmitted to our Site through the internet, or
                collected during the use of our products and services, any such
                transmission is at your own risk. Once LinkShadow have received
                your information, LinkShadow will maintain reasonable
                administrative, physical, technical and organizational measures
                to protect your personal data accessed or processed by
                LinkShadow against unauthorized or unlawful processing or
                accidental loss, destruction, damage or disclosure.
              </p>
              <br />
              <div className="text-xl">Disclosure of your personal data</div>
              <br />
              <p>
                We will not disclose your information to third parties, except
                in the conditions or with the parties set out below:
              </p>
              <br />
              <ul className="ps-4">
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      Within the LinkShadow for the fulfilment of the activities
                      described in (How We May Use User Information & Lawful
                      Basis of Usage).
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      To third parties to whom we may choose to transfer or
                      merge parts of our business or our assets. Alternatively,
                      we may seek to acquire other businesses or merge with
                      them. If a change happens to our business, then the new
                      owners may use your personal data in the same way as set
                      out in this privacy policy.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      To third parties, if we are under a duty to disclose or
                      share your personal data in order to comply with any legal
                      obligation, or in order to enforce or apply our terms of
                      use and other agreements; or to protect the rights,
                      property, or safety of LinkShadow, or others.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      When we have the individual&apos;s consent to make such
                      disclosure.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      When the subject information is publicly available.
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      When release is required by law or regulation, or is
                      requested by a government agency (for example, a
                      subpoena);
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      When the disclosure is reasonably necessary for the
                      establishment or defense of legal claims; or,
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      Upon discovery of fraudulent activity or other deceptive
                      practices if we believe a governmental agency should be
                      notified.
                    </div>
                  </div>
                </li>
              </ul>
              <br />
              <div className="text-xl">Retaining your personal data</div>
              <br />
              <p>
                We will only retain your personal data for as long as reasonably
                necessary to fulfil the purposes we collected it for, including
                for the purposes of satisfying any legal, regulatory, tax,
                accounting or reporting requirements. We may retain your
                personal data for a longer period in the event of a complaint or
                if we reasonably believe there is a prospect of litigation in
                respect to our relationship with you.
              </p>
              <br />
              <p>
                To determine the appropriate retention period for personal data,
                we consider the amount, nature and sensitivity of the personal
                data, the potential risk of harm from unauthorized use or
                disclosure of your personal data, the purposes for which we
                process your personal data and whether we can achieve those
                purposes through other means, and the applicable legal,
                regulatory, tax, accounting or other requirements.
              </p>
              <br />
              <p>
                Details of retention periods for different aspects of your
                personal data can be requested via our Data Privacy Officer at
                the contact details provided below.
              </p>
              <br />
              <div className="text-xl">Know your legal rights</div>
              <br />
              <p>
                GDPR gives you the right to access information held about you.
                Your right of access can be exercised in accordance with the
                GDPR.
              </p>
              <br />
              <p>
                You have the right to request by contacting{" "}
                <a
                  href="mailto:privacy@linkshadow.com"
                  className="color-blue-500"
                >
                  privacy@linkshadow.com
                </a>
              </p>
              <br />
              <ul className="ps-4">
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      Information about how your personal data is processed;
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      A copy of your personal data;
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      An immediate correction to your personal data.
                    </div>
                  </div>
                </li>
              </ul>
              <br />
              <p>You can also:</p>
              <br />
              <ul className="ps-4">
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      Raise an objection about how your personal data is
                      processed;
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      Request that your personal data is erased if there is no
                      longer a justification for it;
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      Ask that the processing of your personal data is
                      restricted in certain circumstances;
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      Opt out of the use of your personal data for any purposes
                      or a specific purpose, such as the LinkShadow Customer
                      Portal.
                    </div>
                  </div>
                </li>
              </ul>
              <br />
              <p>
                As part of Our commitment, none of the collected User
                Information is subject to be sold, and you have the following
                rights as per GDPR:
              </p>
              <br />
              <ul className="ps-4">
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">The right to be informed</div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">The right of access</div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      The right of rectification
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">The right to erasure</div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      The right to restrict processing
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      The right to data portability
                    </div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">The right to object</div>
                  </div>
                </li>
                <br />
                <li>
                  <div className="flex">
                    <div className="inline-block">
                      <CheckCircle
                        className="text-primary inline-block me-2"
                        size={14}
                      />
                    </div>
                    <div className="flex-grow-1">
                      Rights related to automated decision making and profiling
                    </div>
                  </div>
                </li>
                <br />
              </ul>
              <br />
              <p>
                All of which are all included in the body of this Privacy
                Policy.
              </p>
              <br />
              <p>
                You can communicate with our Data Protection Officer with
                regards to any of the above mentioned rights by emailing{" "}
                <a href="mailto:privacy@linkshadow.com">
                  privacy@linkshadow.com
                </a>
              </p>
              <br />
              <div className="text-xl">
                Changes to our Privacy & Data Protection Policy
              </div>
              <br />
              <p>
                LinkShadow reserves the right to amend this Privacy and Data
                Protection Policy at any time, for any reason, without notice to
                you, other than the posting of the amended Privacy and Data
                Protection Policy at this Site. You should check our Site to see
                the current Privacy and Data Protection Policy that is in effect
                and any changes that may have been made to it.
              </p>
              <br />
              <p>This policy was published on 10 September 2021.</p>
            </div>

            {/* Third Page */}
            {(isSystemArchitectureInfoLoading || isDevicePropertiesLoading) &&
            pageNum === 2 ? (
              <div className="flex flex-col items-center justify-center h-64 gap-3">
                <Loader size={32} className="animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">
                  Loading device configuration...
                </p>
              </div>
            ) : (
              <div
                className={
                  "setup-page text-xs " + (pageNum == 2 ? "" : "hidden")
                }
              >
                <div className="text-xl">Device Setup</div>
                <br />
                <div className="">
                  <div className="bg-primary-background p-4 rounded-md">
                    <div className="text-lg mb-4">System Architecture</div>
                    <div
                      className={
                        "mb-2 text-primary " +
                        (formData.arch.value == "" ? "" : "hidden")
                      }
                    >
                      <AlertCircleIcon
                        size={12}
                        className="text-primary me-2 inline-block"
                      />
                      <span>This field is required</span>
                    </div>
                    <Select
                      value={formData.arch.value}
                      onValueChange={(e: any) => {
                        setFormData((prev: any) => ({
                          ...prev,
                          arch: { ...prev.arch, value: e },
                        }));
                      }}
                    >
                      <SelectTrigger className="w-[260px] mb-4">
                        <SelectValue placeholder="Please select architecture" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="standalone">Standalone</SelectItem>
                          <SelectItem value="mssp">MSSP</SelectItem>
                          <SelectItem value="sensors">
                            Master - Sensor
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <div
                      className={
                        "flex items-end space-x-2 my-2 " +
                        (formData.arch.value == "mssp" ||
                        formData.arch.value == "sensors"
                          ? ""
                          : "hidden")
                      }
                    >
                      <Checkbox
                        id="asmaster"
                        checked={formData.master.value}
                        onCheckedChange={(e: boolean) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            master: { ...prev.master, value: e },
                          }));
                        }}
                      />
                      <label
                        htmlFor="asmaster"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Set this device as master
                      </label>
                    </div>
                    <hr />
                    <Button
                      className="mt-4"
                      size="sm"
                      onClick={submitSystemArch}
                    >
                      <span>Submit</span>
                      <Loader
                        className={
                          "ms-2 animate-spin " +
                          (formData.arch.status == "loading" ? "" : "hidden")
                        }
                        size={14}
                      />
                    </Button>
                  </div>

                  <div className="bg-primary-background p-4 rounded-md mt-4">
                    <div className="text-lg mb-4">Device IP Configuration</div>
                    <div className="mb-1">IP Address</div>
                    <div
                      className={
                        "mb-2 text-primary " +
                        (formData.ip.status == "invalid" ? "" : "hidden")
                      }
                    >
                      <AlertCircleIcon
                        size={12}
                        className="text-primary me-2 inline-block"
                      />
                      <span>Valid IP address Required</span>
                    </div>
                    <Input
                      className="w-2/4 mb-4"
                      placeholder="IP Address"
                      ref={ipAddressRef}
                      defaultValue={formData.ip.value}
                      key={`ip-${formData.ip.value}`}
                    />
                    <div className="mb-1">Subnet Mask (CIDR)</div>
                    <div
                      className={
                        "mb-2 text-primary " +
                        (formData.subnet.status == "invalid" ? "" : "hidden")
                      }
                    >
                      <AlertCircleIcon
                        size={12}
                        className="text-primary me-2 inline-block"
                      />
                      <span>Valid Subnet Mask Required</span>
                    </div>
                    <Input
                      className="w-2/4 mb-4"
                      placeholder="Subnet Mask"
                      ref={subnetMaskRef}
                      defaultValue={formData.subnet.value}
                      key={`subnet-${formData.subnet.value}`}
                    />
                    <div className="mb-1">Default Gateway</div>
                    <div
                      className={
                        "mb-2 text-primary " +
                        (formData.gateway.status == "invalid" ? "" : "hidden")
                      }
                    >
                      <AlertCircleIcon
                        size={12}
                        className="text-primary me-2 inline-block"
                      />
                      <span>Valid Gateway required</span>
                    </div>
                    <Input
                      className="w-2/4 mb-4"
                      placeholder="Default Gateway"
                      ref={defaultGatewayRef}
                      defaultValue={formData.gateway.value}
                      key={`gateway-${formData.gateway.value}`}
                    />
                    <div className="mb-1">DNS Server</div>
                    <div
                      className={
                        "mb-2 text-primary " +
                        (formData.dns.status == "invalid" ? "" : "hidden")
                      }
                    >
                      <AlertCircleIcon
                        size={12}
                        className="text-primary me-2 inline-block"
                      />
                      <span>Valid DNS Server required</span>
                    </div>
                    <Input
                      className="w-2/4 mb-4"
                      placeholder="DNS Server"
                      ref={dnsServerRef}
                      defaultValue={formData.dns.value}
                      key={`dns-${formData.dns.value}`}
                    />
                    <hr />
                    <Button className="mt-4" size="sm" onClick={submitipConfig}>
                      <span>Submit</span>
                      <Loader
                        className={
                          "ms-2 animate-spin " +
                          (formData.ip.status == "loading" ? "" : "hidden")
                        }
                        size={14}
                      />
                    </Button>
                  </div>

                  <div className="bg-primary-background p-4 rounded-md mt-4">
                    <div className="text-lg mb-4">Time Configuration</div>
                    <div
                      className={
                        "mb-2 text-primary " +
                        (formData.timezone.status == "invalid" ? "" : "hidden")
                      }
                    >
                      <AlertCircleIcon
                        size={12}
                        className="text-primary me-2 inline-block"
                      />
                      <span>Valid Timezone is required</span>
                    </div>
                    <div className="mb-1">Timezone</div>
                    <div className="w-[300px] mb-4">
                      <CustomSelect
                        onSelect={(e: any) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            timezone: {
                              ...prev.timezone,
                              value: e,
                              status: e.trim() != "" ? "valid" : "invalid",
                            },
                          }));
                        }}
                        data={timezoneValues}
                        placeholder="Timezone"
                        defaultValue={formData.timezone.value}
                      />
                    </div>
                    <hr />
                    <Button className="mt-4" size="sm" onClick={submitTimeZone}>
                      <span>Submit</span>
                      <Loader
                        className={
                          "ms-2 animate-spin " +
                          (formData.timezone.status == "loading"
                            ? ""
                            : "hidden")
                        }
                        size={14}
                      />
                    </Button>
                  </div>

                  <div className="bg-primary-background p-4 rounded-md mt-4">
                    <div className="text-lg mb-4">Airgap Deployment</div>
                    <div className="flex items-center space-x-2 mb-4">
                      <Switch
                        id="airplane-mode"
                        checked={formData.airgap.value}
                        onCheckedChange={(e) => submitAirgap(e)}
                      />
                      <Label htmlFor="airplane-mode">Mode</Label>
                      <Loader
                        className={
                          "ms-2 animate-spin " +
                          (formData.airgap.status == "loading" ? "" : "hidden")
                        }
                        size={14}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <div className="w-full grid grid-cols-12">
              <div className="col-span-7">
                <div
                  className={
                    "flex items-center space-x-2 mt-3 " +
                    (pageNum == 1 ? "" : "hidden")
                  }
                >
                  <Checkbox
                    id="terms"
                    onCheckedChange={(e: boolean) => termCheckChanged(e)}
                  />
                  <label
                    htmlFor="terms"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    I have read and agree to the Privacy Policy
                  </label>
                </div>
                <div
                  id="termsError"
                  className="hidden mt-2 text-primary text-xs"
                >
                  <AlertCircle className="inline me-2" size={14} />
                  <span>Please check this box if you want to proceed</span>
                </div>
              </div>
              <div className="col-span-5 text-right">
                <Button
                  variant="secondary"
                  className={"align-top me-2 " + (pageNum == 0 ? "hidden" : "")}
                  onClick={() => changePage(pageNum - 1)}
                >
                  <ArrowLeftIcon className="h-5 w-5 me-2" />
                  <span className="font-bold">Previous</span>
                </Button>

                <Button
                  className={"align-top " + (pageNum < 2 ? "" : "hidden")}
                  onClick={() => changePage(pageNum + 1)}
                >
                  <span className="font-bold">Next</span>
                  <ArrowRightIcon className="h-5 w-5 ml-2" />
                </Button>

                <Button
                  className={"align-top " + (pageNum == 2 ? "" : "hidden")}
                  onClick={() => finishSetup()}
                >
                  <span className="font-bold">Finish</span>
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SetupWizard;
